import axios, { isAxiosError } from 'axios';
import {
  fetchRuntimeActiveSession,
  startRuntimeSession,
  type RuntimeActiveSession,
  type RuntimeSessionStartResponse,
} from './quotaRuntime.client';

/** Client timeout for runtime POST/GET (defence in depth; reconciliation handles longer server work). */
export const RUNTIME_START_CLIENT_TIMEOUT_MS = 18_000;

/** Max wall-clock wait after ambiguous POST /session/start before giving up. */
export const RUNTIME_RECONCILE_MAX_MS = 55_000;

export const RUNTIME_RECONCILE_INITIAL_DELAY_MS = 750;

export const RUNTIME_RECONCILE_POLL_DELAYS_MS = [1_000, 1_500, 2_000, 2_500, 3_000] as const;

export type EnsureRuntimeSessionPath =
  | 'existing_active'
  | 'started'
  | 'adopted_409'
  | 'reconciled_ambiguous'
  | 'legacy_retry';

export type EnsureRuntimeSessionResult =
  | { ok: true; session: RuntimeActiveSession | RuntimeSessionStartResponse; path: EnsureRuntimeSessionPath }
  | { ok: false; reason: 'quota' | 'failed'; error?: unknown };

export type EnsureRuntimeSessionParams = {
  featureName: string;
  tutorType: string;
  lessonNo?: number;
  /** Malay activity key for embedded token validation on Runtime Start. */
  activityKey?: string | null;
  /** Stable per Start Class attempt — backend dedupes repeated POST /session/start. */
  idempotencyKey?: string;
  adoptSession: (session: RuntimeActiveSession | RuntimeSessionStartResponse) => void;
  /** When true, a POST /session/start may still be completing server-side — do not POST again. */
  postMayStillCompleteRef: { current: boolean };
  signal?: AbortSignal;
  isStale?: () => boolean;
  /** When false, caller retains legacy retry loop (TeacherLive). */
  reconciliationEnabled?: boolean;
  /**
   * When true, skip the initial GET /runtime/session/active before POST.
   * Use only for a verified fresh Start Class (not reconnect / ambiguous recovery).
   */
  skipInitialActiveCheck?: boolean;
};

/**
 * Feature flag: reconciliation on by default; set VITE_TUTOR_RUNTIME_START_RECONCILE=0 to disable.
 */
export function isRuntimeStartReconciliationEnabled(): boolean {
  if (typeof import.meta !== 'undefined' && import.meta.env?.VITE_TUTOR_RUNTIME_START_RECONCILE === '0') {
    return false;
  }
  return true;
}

function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException('Aborted', 'AbortError'));
      return;
    }
    const host = globalThis as typeof globalThis & {
      setTimeout: typeof setTimeout;
      clearTimeout: typeof clearTimeout;
    };
    const timer = host.setTimeout(() => resolve(), ms);
    signal?.addEventListener(
      'abort',
      () => {
        host.clearTimeout(timer);
        reject(new DOMException('Aborted', 'AbortError'));
      },
      { once: true },
    );
  });
}

function readHttpError(error: unknown): { status?: number; code?: string } {
  if (!isAxiosError(error)) return {};
  const status = error.response?.status;
  const data = error.response?.data as { detail?: { code?: string } } | undefined;
  return {
    status,
    code: data?.detail?.code,
  };
}

/**
 * True when POST /runtime/session/start outcome is inconclusive (timeout, network, status 0).
 * Definitive HTTP responses (401, 403, 409, 429, …) are not ambiguous.
 */
export function isAmbiguousRuntimeStartError(error: unknown): boolean {
  if (!error) return false;
  if (!isAxiosError(error)) {
    const msg = String((error as { message?: string })?.message ?? error).toLowerCase();
    return msg.includes('network error') || msg.includes('timeout');
  }

  const status = error.response?.status;
  if (status != null && status > 0) {
    return false;
  }

  const code = error.code;
  if (code === 'ECONNABORTED' || code === 'ERR_NETWORK' || code === 'ETIMEDOUT') {
    return true;
  }

  const msg = (error.message ?? '').toLowerCase();
  if (msg.includes('timeout')) {
    return true;
  }

  // Request sent but no HTTP response (browser status 0 / ERR_ABORTED).
  if (error.request && !error.response) {
    return true;
  }

  return false;
}

export function isDefinitiveQuotaDeniedError(error: unknown): boolean {
  const { status, code } = readHttpError(error);
  return status === 429 || code === 'MONTHLY_TIME_LIMIT_EXCEEDED';
}

export function isActiveSessionExistsError(error: unknown): boolean {
  const { status, code } = readHttpError(error);
  return status === 409 || code === 'ACTIVE_SESSION_EXISTS';
}

async function adoptIfActive(
  featureName: string,
  adoptSession: (session: RuntimeActiveSession) => void,
): Promise<RuntimeActiveSession | null> {
  const existing = await fetchRuntimeActiveSession(featureName);
  if (existing?.session_id && existing.status === 'ACTIVE') {
    adoptSession(existing);
    return existing;
  }
  return null;
}

/**
 * Poll GET /runtime/session/active after an ambiguous POST start (no second POST).
 */
export async function pollRuntimeActiveSessionAfterAmbiguousStart(
  featureName: string,
  opts?: {
    signal?: AbortSignal;
    isStale?: () => boolean;
    maxDurationMs?: number;
    onPoll?: (attempt: number) => void;
  },
): Promise<RuntimeActiveSession | null> {
  const maxMs = opts?.maxDurationMs ?? RUNTIME_RECONCILE_MAX_MS;
  const startedAt = Date.now();

  try {
    await sleep(RUNTIME_RECONCILE_INITIAL_DELAY_MS, opts?.signal);
  } catch {
    return null;
  }

  let attempt = 0;
  while (Date.now() - startedAt < maxMs) {
    if (opts?.signal?.aborted || opts?.isStale?.()) {
      return null;
    }

    opts?.onPoll?.(attempt);

    try {
      const existing = await fetchRuntimeActiveSession(featureName);
      if (existing?.session_id && existing.status === 'ACTIVE') {
        return existing;
      }
    } catch {
      /* keep polling — active GET may also be slow */
    }

    const delay =
      RUNTIME_RECONCILE_POLL_DELAYS_MS[
        Math.min(attempt, RUNTIME_RECONCILE_POLL_DELAYS_MS.length - 1)
      ];
    attempt += 1;

    try {
      await sleep(delay, opts?.signal);
    } catch {
      return null;
    }
  }

  return null;
}

/**
 * Start or recover a Quota Runtime V2 session for Start Class.
 * When reconciliation is enabled, ambiguous POST results poll GET /active without a second POST.
 */
export async function ensureRuntimeSessionForStart(
  params: EnsureRuntimeSessionParams,
): Promise<EnsureRuntimeSessionResult> {
  const {
    featureName,
    tutorType,
    lessonNo,
    activityKey,
    idempotencyKey,
    adoptSession,
    postMayStillCompleteRef,
    signal,
    isStale,
    reconciliationEnabled = isRuntimeStartReconciliationEnabled(),
    skipInitialActiveCheck = false,
  } = params;

  if (!skipInitialActiveCheck) {
    const existing = await adoptIfActive(featureName, adoptSession);
    if (existing) {
      return { ok: true, session: existing, path: 'existing_active' };
    }
  }

  if (postMayStillCompleteRef.current && reconciliationEnabled) {
    const reconciled = await pollRuntimeActiveSessionAfterAmbiguousStart(featureName, {
      signal,
      isStale,
      onPoll: (attempt) => {
        console.info(
          '[TUTOR_LIVE_QUOTA_DIAG]',
          JSON.stringify({
            event: 'runtime_session_reconcile_poll',
            attempt: attempt + 1,
            feature_name: featureName,
          }),
        );
      },
    });
    postMayStillCompleteRef.current = false;
    if (reconciled) {
      adoptSession(reconciled);
      return { ok: true, session: reconciled, path: 'reconciled_ambiguous' };
    }
    return { ok: false, reason: 'failed' };
  }

  postMayStillCompleteRef.current = true;

  try {
    const started = await startRuntimeSession({
      feature_name: featureName,
      tutor_type: tutorType,
      lesson_no: lessonNo,
      activity: activityKey ?? undefined,
      idempotency_key: idempotencyKey,
    });
    postMayStillCompleteRef.current = false;
    adoptSession(started);
    return { ok: true, session: started, path: 'started' };
  } catch (error: unknown) {
    if (isDefinitiveQuotaDeniedError(error)) {
      postMayStillCompleteRef.current = false;
      return { ok: false, reason: 'quota', error };
    }

    if (isActiveSessionExistsError(error)) {
      try {
        const adopted = await adoptIfActive(featureName, adoptSession);
        postMayStillCompleteRef.current = false;
        if (adopted) {
          return { ok: true, session: adopted, path: 'adopted_409' };
        }
      } catch (adoptErr) {
        postMayStillCompleteRef.current = false;
        return { ok: false, reason: 'failed', error: adoptErr };
      }
    }

    const { status } = readHttpError(error);
    if (status != null && status > 0 && !isActiveSessionExistsError(error)) {
      postMayStillCompleteRef.current = false;
      return { ok: false, reason: 'failed', error };
    }

    if (reconciliationEnabled && isAmbiguousRuntimeStartError(error)) {
      console.warn(
        '[TUTOR_LIVE_QUOTA_DIAG]',
        JSON.stringify({
          event: 'runtime_session_start_ambiguous',
          feature_name: featureName,
          code: isAxiosError(error) ? error.code : null,
          message: isAxiosError(error) ? error.message : String(error),
        }),
      );

      const reconciled = await pollRuntimeActiveSessionAfterAmbiguousStart(featureName, {
        signal,
        isStale,
        onPoll: (attempt) => {
          console.info(
            '[TUTOR_LIVE_QUOTA_DIAG]',
            JSON.stringify({
              event: 'runtime_session_reconcile_poll',
              attempt: attempt + 1,
              feature_name: featureName,
            }),
          );
        },
      });
      postMayStillCompleteRef.current = false;
      if (reconciled) {
        adoptSession(reconciled);
        return { ok: true, session: reconciled, path: 'reconciled_ambiguous' };
      }
      return { ok: false, reason: 'failed', error };
    }

    postMayStillCompleteRef.current = false;
    return { ok: false, reason: 'failed', error };
  }
}

/** Test helper — detect axios-like errors without DOM. */
export function isAmbiguousRuntimeStartErrorForTest(error: unknown): boolean {
  if (axios.isAxiosError(error)) {
    return isAmbiguousRuntimeStartError(error);
  }
  return isAmbiguousRuntimeStartError(error);
}
